import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

const EmailPreviewModal = ({ invoiceId, isOpen, onClose }) => {
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && invoiceId) {
      fetchEmailPreview();
    }
  }, [isOpen, invoiceId]);

  const fetchEmailPreview = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_BASE_URL}/billing/invoice/email-preview/${invoiceId}`);
      
      if (response.data.success) {
        setEmailData(response.data.email);
      } else {
        setError('Failed to load email preview');
      }
    } catch (err) {
      console.error('Error fetching email preview:', err);
      setError(err.response?.data?.message || 'Email preview not available');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-envelope me-2"></i>
              Email Preview
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div className="modal-body">
            {loading && (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading email preview...</p>
              </div>
            )}
            
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}
            
            {emailData && (
              <div>
                {/* Email Headers */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Email Details</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <strong>Invoice:</strong> {emailData.invoiceNumber}
                      </div>
                      <div className="col-md-6">
                        <strong>Client:</strong> {emailData.clientName}
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <strong>Sent:</strong> {formatDate(emailData.sentAt)}
                      </div>
                      <div className="col-md-6">
                        <strong>To:</strong> {emailData.to}
                      </div>
                    </div>
                    {emailData.cc && (
                      <div className="row mt-2">
                        <div className="col-12">
                          <strong>CC:</strong> {emailData.cc}
                        </div>
                      </div>
                    )}
                    {emailData.bcc && (
                      <div className="row mt-2">
                        <div className="col-12">
                          <strong>BCC:</strong> {emailData.bcc}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Email Subject */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Subject</h6>
                  </div>
                  <div className="card-body">
                    <p className="mb-0">{emailData.subject}</p>
                  </div>
                </div>

                {/* Email Content */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Message</h6>
                  </div>
                  <div className="card-body">
                    <div 
                      className="border rounded p-3 bg-light"
                      style={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        minHeight: '200px'
                      }}
                    >
                      {emailData.content}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewModal;